import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Footer() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('company_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }
    try {
      const res = await fetch('https://readdy.ai/api/form/d97oo7i4irb0s7p1uv8g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }
      const code = (parsed?.code as string) || '';
      const serverMsg = ((parsed?.meta as Record<string, unknown>)?.message as string) || (parsed?.message as string) || '';
      const detailMsg = ((parsed?.meta as Record<string, unknown>)?.detail as string) || '';
      const isSpam = serverMsg.includes('spam') || detailMsg.includes('spam') || text.includes('spam');
      if (res.ok && code === 'OK' && !isSpam) {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(serverMsg || detailMsg || 'Something went wrong. Please try again.');
      }
    } catch {
      setFormStatus('error');
      setFormError('Network error. Please try again.');
    }
  };

  return (
    <footer className="bg-foreground-900 text-white">
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]">
          <div className="absolute top-0 left-0 w-full h-full" style={{
            backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(255,255,255,0.3) 1px, transparent 1px), radial-gradient(circle at 60% 70%, rgba(255,255,255,0.2) 1px, transparent 1px)',
            backgroundSize: '60px 60px, 80px 80px',
          }}></div>
        </div>

        <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-10 py-16 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            <div className="lg:col-span-2">
              <Link to="/" className="flex items-center gap-1.5 mb-6">
                <span className="font-heading text-xl font-semibold tracking-tight">
                  Hipobuy Spreadsheet
                </span>
                <span className="w-1.5 h-1.5 rounded-full bg-primary-400 flex-shrink-0"></span>
              </Link>

              <div className="mb-6 max-w-[340px]">
                <form ref={formRef} onSubmit={handleSubmit} data-readdy-form>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <input
                        type="email"
                        name="email"
                        placeholder="Your email for updates"
                        required
                        className="w-full px-4 py-2.5 rounded-full bg-white/10 border border-white/15 text-white text-sm placeholder:text-white/35 focus:outline-none focus:border-white/30 transition-colors"
                      />
                    </div>
                    <button
                      type="submit"
                      className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-colors cursor-pointer flex-shrink-0"
                    >
                      <i className="ri-notification-3-line text-lg"></i>
                    </button>
                  </div>
                  <div className="form-honeypot">
                    <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>
                  {formStatus === 'success' && (
                    <p className="text-primary-400 text-xs mt-2">Subscribed! Thanks for joining.</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-accent-400 text-xs mt-2">{formError}</p>
                  )}
                </form>
              </div>

              <p className="font-heading text-lg md:text-xl text-white/60 leading-relaxed">
                Smarter Shopping.
                <br />
                Global Finds.
              </p>
            </div>

            <div>
              <h4 className="font-label text-sm font-semibold text-white/50 uppercase tracking-wider mb-4">Hub</h4>
              <ul className="space-y-2.5">
                <li><Link to="/" className="text-sm text-white/60 hover:text-white transition-colors">Home</Link></li>
                <li><Link to="/article/hipobuy-spreadsheet-guide" className="text-sm text-white/60 hover:text-white transition-colors">Complete Guide</Link></li>
                <li><Link to="/article/hipobuy-vs-kakobuy-comparison" className="text-sm text-white/60 hover:text-white transition-colors">Comparison Guide</Link></li>
                <li><Link to="/article/hipobuy-safety-guide" className="text-sm text-white/60 hover:text-white transition-colors">Safety Guide</Link></li>
                <li><a href="#categories" className="text-sm text-white/60 hover:text-white transition-colors">All Categories</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-label text-sm font-semibold text-white/50 uppercase tracking-wider mb-4">Alternatives</h4>
              <ul className="space-y-2.5">
                <li><a href="#" className="text-sm text-white/60 hover:text-white transition-colors">Kakobuy Spreadsheet</a></li>
                <li><a href="#" className="text-sm text-white/60 hover:text-white transition-colors">CSSBuy Spreadsheet</a></li>
                <li><a href="#" className="text-sm text-white/60 hover:text-white transition-colors">Superbuy Spreadsheet</a></li>
                <li><a href="#" className="text-sm text-white/60 hover:text-white transition-colors">Oopbuy Spreadsheet</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-label text-sm font-semibold text-white/50 uppercase tracking-wider mb-4">Categories</h4>
              <ul className="space-y-2.5">
                {categories.slice(0, 7).map((cat) => (
                  <li key={cat.name}>
                    <a href={cat.url} target="_blank" rel="noopener nofollow" className="text-sm text-white/60 hover:text-white transition-colors">
                      {cat.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-14 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-white/35">
              &copy; {new Date().getFullYear()} Hipobuy Spreadsheet Guide. All rights reserved.
            </p>
            <div className="flex items-center gap-3">
              {['twitter-x-line', 'instagram-line', 'youtube-line', 'pinterest-line'].map((icon) => (
                <a
                  key={icon}
                  href="#"
                  target="_blank"
                  rel="noopener nofollow"
                  className="w-8 h-8 flex items-center justify-center rounded-md border border-white/10 text-white/40 hover:text-white hover:border-white/25 transition-colors cursor-pointer"
                >
                  <i className={`ri-${icon} text-sm`}></i>
                </a>
              ))}
            </div>
            <a href="#" className="text-xs text-white/35 hover:text-white/60 transition-colors">Privacy Policy</a>
          </div>
        </div>
      </div>
    </footer>
  );
}